/**
 * Utility functions for extracting fields from item objects.
 *
 * The codebase has two different item structures:
 * - Items API: { id, latest_analysis: { headline, category, ... } }
 * - Search API: { item_id, headline, category, ... } (flat)
 *
 * These helpers provide a consistent way to access fields regardless of structure.
 */

/**
 * Get the analysis object from an item.
 * Handles both nested (latest_analysis) and flat structures.
 */
export const getItemAnalysis = (item) =>
  item.latest_analysis || item.analysis || item;

/**
 * Get the category from an item.
 */
export const getItemCategory = (item) => {
  const analysis = getItemAnalysis(item);
  return analysis.category || item.category || null;
};

/**
 * score_type values whose score is a genuine, absolute 0-1 relevance signal
 * (VoyageAI rerank score, or true cosine similarity from vector search).
 * "bm25"/"hybrid_rrf" without reranking are batch-relative instead — the
 * top result in ANY search is normalized to ~1.0 regardless of how weak the
 * match actually is, so a "% match"/relevance display would mislead the
 * user for those types (see backend shared/CLAUDE.md's Retrieval System
 * section, COL-37). Shared by ItemCard and ChatItemCard.
 */
const ABSOLUTE_SCORE_TYPES = new Set(['rerank', 'vector', 'vector_threshold']);

/**
 * Whether an item's score is safe to render as an absolute percentage/
 * relevance indicator (vs. a batch-relative score that's only meaningful
 * within its own search result set).
 */
export const hasAbsoluteScore = (item) =>
  item.score !== undefined &&
  item.score !== null &&
  ABSOLUTE_SCORE_TYPES.has(item.score_type);

/**
 * Cosine-similarity band thresholds for `vector`/`vector_threshold` scores
 * (COL-172). Unlike `rerank`, these scores are cosine similarity, whose
 * typical VoyageAI range is 0.2-0.5 (docs/search-capabilities.md:882-890)
 * with a 0.4 default "match" threshold (pgvector_store.py:270) -- so the raw
 * number reads as weak even for a strong hit. Bands anchor on that
 * documented range rather than remapping the number into a fake percentage.
 */
const VECTOR_SCORE_BANDS = [
  { min: 0.5, label: 'Strong match' },
  { min: 0.4, label: 'Good match' },
  { min: 0.3, label: 'Related' },
];

/**
 * Get the display label for an item's score footer, or null if nothing
 * should render. Single source of truth for the score-display numbers so
 * they never drift between ItemCard and any other caller:
 *  - `rerank`: genuine 0-1 relevance -> `NN% match`, unchanged.
 *  - `vector`/`vector_threshold`: cosine similarity -> a banded label
 *    (see VECTOR_SCORE_BANDS); below the lowest band, render nothing --
 *    the item is still a real result, the footer just doesn't editorialise.
 *  - Batch-relative types (`hybrid_rrf`, `bm25`) and anything without an
 *    absolute score: null, same as today.
 */
export const getScoreLabel = (item) => {
  if (!hasAbsoluteScore(item)) return null;

  if (item.score_type === 'rerank') {
    return `${Math.round(item.score * 100)}% match`;
  }

  const band = VECTOR_SCORE_BANDS.find(({ min }) => item.score >= min);
  return band ? band.label : null;
};

/**
 * Get the headline from an item.
 */
export const getItemHeadline = (item) => {
  const analysis = getItemAnalysis(item);
  return analysis.headline || item.headline || 'Untitled';
};

/**
 * Get the raw_response from an item's analysis.
 */
export const getItemRawResponse = (item) => {
  const analysis = getItemAnalysis(item);
  return analysis.raw_response || item.raw_response || {};
};

/**
 * Get a short summary from an item's analysis (same field precedence as
 * ItemDetailModal's detail view: `summary` first, falling back to
 * `description`). Used for the 2-line summary shown on ItemCard.
 */
export const getItemSummary = (item) => {
  const analysis = getItemAnalysis(item);
  return analysis.summary || analysis.description || item.summary || item.description || '';
};

/**
 * Get subcategories from an item.
 */
export const getItemSubcategories = (item) => {
  const analysis = getItemAnalysis(item);
  const rawResponse = getItemRawResponse(item);
  return analysis.subcategories || rawResponse.subcategories || [];
};

/**
 * Get structured_data from an item's analysis.
 * Returns the structured data object (recipe, workout, itinerary, checklist, steps) or null.
 */
export const getItemStructuredData = (item) => {
  const analysis = getItemAnalysis(item);
  return analysis.structured_data || null;
};

/**
 * Get a de-duplicated, display-ready list of entity names from an item's
 * enrichments, for the card's entity chips (COL-92). The backend batch-attaches
 * `enrichments` onto every item — both `/items` (list) and `/items/{id}`
 * (detail) — in `_item_with_analysis` (lambdas/api/services/item_service.py),
 * so this reads data already on the item; it never triggers a per-card fetch.
 * The flat Search API item shape has no per-item `enrichments` array, so this
 * returns [] there rather than guessing at an entity list.
 */
export const getItemEntityNames = (item) => {
  const enrichments = item.enrichments;
  if (!Array.isArray(enrichments)) return [];
  const seen = new Set();
  const names = [];
  for (const enrichment of enrichments) {
    const name = enrichment?.entity_name;
    if (name && !seen.has(name)) {
      seen.add(name);
      names.push(name);
    }
  }
  return names;
};

/**
 * Get music-provider links from an item's enrichments (COL-286), for
 * rendering Apple Music / Spotify links on the grid card. Each URL is taken
 * from the first enrichment carrying it, independently per field (COL-311) —
 * a multi-entity item can resolve Apple on one entity and Spotify on another,
 * and both must surface rather than only whichever entity comes first. Same
 * `item.enrichments` data source as `getItemEntityNames` above, so no extra
 * fetch. Flat Search API items have no per-item `enrichments` array and
 * simply get {} back.
 */
export const getItemMusicLinks = (item) => {
  const enrichments = item.enrichments;
  if (!Array.isArray(enrichments)) return {};
  const appleMusicUrl = enrichments.find((e) => e?.apple_music_url)?.apple_music_url;
  const spotifyUrl = enrichments.find((e) => e?.spotify_url)?.spotify_url;
  if (!appleMusicUrl && !spotifyUrl) return {};
  return { appleMusicUrl, spotifyUrl };
};

/**
 * Non-subscription places to listen, in the order they're offered to a user
 * who has no music service. Matched on hostname suffix so
 * `music.youtube.com` and `youtu.be` both resolve to YouTube.
 */
const OTHER_MUSIC_SOURCES = [
  { provider: 'youtube', label: 'YouTube', hosts: ['youtube.com', 'youtu.be'] },
  { provider: 'soundcloud', label: 'SoundCloud', hosts: ['soundcloud.com'] },
  { provider: 'bandcamp', label: 'Bandcamp', hosts: ['bandcamp.com'] },
  { provider: 'mixcloud', label: 'Mixcloud', hosts: ['mixcloud.com'] },
  { provider: 'audiomack', label: 'Audiomack', hosts: ['audiomack.com'] },
];

/**
 * Schema.org types the enricher resolves music-provider links for
 * (MUSIC_SCHEMA_TYPES in lambdas/enricher/service.py). Used here only to
 * decide whether a web-search URL on an enrichment is a music link worth
 * offering, never to change what gets enriched.
 */
const MUSIC_SCHEMA_TYPES = new Set([
  'MusicRecording',
  'MusicAlbum',
  'MusicVideoObject',
  'MusicGroup',
  'MusicArtist',
  'Podcast',
  'PodcastEpisode',
]);

const isMusicEnrichment = (enrichment) =>
  !!enrichment &&
  (!!enrichment.apple_music_url ||
    !!enrichment.spotify_url ||
    MUSIC_SCHEMA_TYPES.has(enrichment.schema_org_type));

const matchOtherMusicSource = (url) => {
  let hostname;
  try {
    hostname = new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
  return (
    OTHER_MUSIC_SOURCES.find(({ hosts }) =>
      hosts.some((host) => hostname === host || hostname.endsWith(`.${host}`))
    ) || null
  );
};

/**
 * Get listen-here links that aren't Apple Music or Spotify (YouTube,
 * SoundCloud, …) from an item's music enrichments, for users whose Settings
 * choice is "I don't have a music service".
 *
 * These aren't a dedicated backend field — the enricher already stores
 * Google/Tavily results on the same music enrichment (`primary_result`,
 * `additional_results`, `exploration_links`), so this reads those URLs and
 * keeps the ones pointing at a known listening source, one per provider in
 * OTHER_MUSIC_SOURCES order. Same `item.enrichments` data source as
 * `getItemMusicLinks`, so no extra fetch; flat Search API items get [].
 */
export const getItemOtherMusicLinks = (item) => {
  const enrichments = item.enrichments;
  if (!Array.isArray(enrichments)) return [];
  return getEnrichmentOtherMusicLinks(enrichments);
};

/**
 * Same extraction as `getItemOtherMusicLinks`, for callers that already hold
 * the enrichment(s) — e.g. the per-entity cards in ItemDetailModal. The
 * music-entity gate lives here rather than in the callers so a restaurant
 * whose search results happen to include a YouTube tour can't pick up a
 * "listen on YouTube" link on one surface but not the other.
 */
export const getEnrichmentOtherMusicLinks = (enrichments) => {
  const found = new Map();
  for (const enrichment of enrichments.filter(isMusicEnrichment)) {
    const urls = [
      enrichment?.primary_result?.url,
      ...(enrichment?.additional_results || []).map((r) => r?.url),
      ...(enrichment?.exploration_links || []).map((l) => l?.url),
    ];
    for (const url of urls) {
      if (!url) continue;
      const source = matchOtherMusicSource(url);
      if (source && !found.has(source.provider)) {
        found.set(source.provider, { provider: source.provider, label: source.label, url });
      }
    }
  }
  return OTHER_MUSIC_SOURCES.filter(({ provider }) => found.has(provider)).map(({ provider }) =>
    found.get(provider)
  );
};

/**
 * Get a human-readable platform name from a likely_source string.
 */
export const getPlatformName = (source) => {
  if (!source) return null;
  const s = source.toLowerCase();
  if (s.includes('tiktok')) return 'TikTok';
  if (s.includes('instagram')) return 'Instagram';
  if (s.includes('youtube')) return 'YouTube';
  return source;
};
